/**
 * House-rules editor shared by the home screen (choosing rules for a new table)
 * and the lobby (the host adjusting them before the first deal).
 */

import { DEFAULT_RULES, RULE_CHOICES, type RulesConfig, rulesEqual } from '@euchre/core';
import { h } from '../dom.js';

export interface RulesEditorOptions {
  editable: boolean;
  /** Called with the changed fields only. */
  onChange: (patch: Partial<RulesConfig>) => void;
  /** Show a "Standard rules" reset button (editable only). */
  showReset?: boolean;
}

let uid = 0;

/** One plain-language line describing a table's rules, e.g. "Standard rules, first to 10 points". */
export function rulesSummary(r: RulesConfig): string {
  if (rulesEqual(r, { ...DEFAULT_RULES, targetScore: r.targetScore })) return `Standard rules, first to ${r.targetScore} points`;
  const parts = [`First to ${r.targetScore}`];
  if (r.stickTheDealer) parts.push('stick the dealer');
  if (r.allowDefendAlone) parts.push(`defenders may go alone (${r.loneDefenderEuchrePoints} pts)`);
  if (r.noAceNoFaceRedeal) parts.push('no ace, no face');
  if (r.farmersHand) parts.push(`farmer’s hand (${r.farmersHandMinLowCards} nines and tens)`);
  return parts.join(' · ');
}

export function rulesEditor(r: RulesConfig, opts: RulesEditorOptions): HTMLElement {
  const { editable, onChange } = opts;
  const id = `rules-${++uid}`;

  const check = (key: 'stickTheDealer' | 'allowDefendAlone' | 'noAceNoFaceRedeal' | 'farmersHand', label: string, hint: string) =>
    h(
      'div',
      { class: 'rule' },
      h(
        'label',
        { class: 'check' },
        h('input', {
          type: 'checkbox',
          name: key,
          checked: r[key],
          disabled: !editable,
          onchange: (ev) => onChange({ [key]: (ev.target as HTMLInputElement).checked }),
        }),
        ` ${label}`,
      ),
      h('p', { class: 'rule-hint', text: hint }),
    );

  const select = <K extends 'targetScore' | 'loneDefenderEuchrePoints' | 'farmersHandMinLowCards'>(
    key: K,
    choices: readonly number[],
    text: (n: number) => string,
    enabled = true,
  ) => {
    const values = choices.includes(r[key]) ? choices : [...choices, r[key]].sort((a, b) => a - b);
    const el = h(
      'select',
      {
        class: 'field inline',
        name: key,
        id: `${id}-${key}`,
        disabled: !editable || !enabled,
        onchange: (ev) => onChange({ [key]: Number((ev.target as HTMLSelectElement).value) } as Partial<RulesConfig>),
      },
      values.map((n) => {
        const opt = h('option', { value: String(n), text: text(n) });
        opt.selected = n === r[key];
        return opt;
      }),
    );
    return el;
  };

  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

  return h(
    'div',
    { class: 'rules-editor' },
    h(
      'div',
      { class: 'rule' },
      h('label', { class: 'check', htmlFor: `${id}-targetScore` }, 'Play to ', select('targetScore', RULE_CHOICES.targetScore, (n) => plural(n, 'point'))),
    ),
    check('stickTheDealer', 'Stick the dealer', 'If everyone passes twice, the dealer must name trump. Off: the hand is thrown in and the deal moves left.'),
    check('allowDefendAlone', 'Defenders may go alone', 'After trump is called, a defender may play without their partner.'),
    h(
      'div',
      { class: 'rule sub' },
      h(
        'label',
        { class: 'check', htmlFor: `${id}-loneDefenderEuchrePoints` },
        'A lone defender’s euchre scores ',
        select('loneDefenderEuchrePoints', RULE_CHOICES.loneDefenderEuchrePoints, (n) => plural(n, 'point'), r.allowDefendAlone),
      ),
    ),
    check('noAceNoFaceRedeal', 'No ace, no face', 'A player dealt only nines and tens may ask for a redeal.'),
    check('farmersHand', 'Farmer’s hand', 'A player dealt enough nines and tens may swap three of them for the kitty.'),
    h(
      'div',
      { class: 'rule sub' },
      h(
        'label',
        { class: 'check', htmlFor: `${id}-farmersHandMinLowCards` },
        'Needs at least ',
        select('farmersHandMinLowCards', RULE_CHOICES.farmersHandMinLowCards, (n) => `${n} nines and tens`, r.farmersHand),
      ),
    ),
    editable && opts.showReset
      ? h('button', {
          class: 'btn quiet small',
          type: 'button',
          text: 'Back to standard rules',
          disabled: rulesEqual(r, DEFAULT_RULES),
          onclick: () => onChange({ ...DEFAULT_RULES }),
        })
      : null,
  );
}
